import { useRef, type MouseEvent as ReactMouseEvent } from 'react'

/**
 * Reusable hook to enable click-and-drag horizontal scrolling for mouse users.
 * Respects click events on children by using a drag threshold and blocking
 * clicks only if a drag gesture occurred.
 */
export const useDragScroll = () => {
  const ref = useRef<HTMLDivElement>(null)
  const isDraggingRef = useRef(false)
  const startXRef = useRef(0)
  const scrollLeftRef = useRef(0)

  const onMouseDown = (e: ReactMouseEvent<HTMLDivElement>) => {
    const el = ref.current
    if (!el) return

    startXRef.current = e.pageX
    scrollLeftRef.current = el.scrollLeft
    isDraggingRef.current = false

    // Prevent browser image/button native drag-and-drop from interrupting custom gesture
    const onDragStart = (dragEvent: Event) => {
      dragEvent.preventDefault()
    }
    el.addEventListener('dragstart', onDragStart)

    const onMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.pageX - startXRef.current
      if (!isDraggingRef.current && Math.abs(deltaX) > 5) {
        isDraggingRef.current = true
        el.style.cursor = 'grabbing'
        el.style.scrollBehavior = 'auto'
      }

      if (isDraggingRef.current) {
        el.scrollLeft = scrollLeftRef.current - deltaX * 1.5
      }
    }

    const onMouseUp = () => {
      if (isDraggingRef.current) {
        el.style.cursor = 'grab'
        el.style.scrollBehavior = ''
      }
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      el.removeEventListener('dragstart', onDragStart)
    }

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }

  const onClickCapture = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (isDraggingRef.current) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  return {
    ref,
    onMouseDown,
    onClickCapture,
    style: {
      cursor: 'grab',
      userSelect: 'none' as const,
      WebkitUserSelect: 'none' as const,
    },
  }
}
